import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import { glossaryService, type GlossaryTermSummary, type GlossaryTermCreateRequest } from '../../services/glossaryService';

const DIFFICULTIES = ['starter', 'intermediate', 'advanced'] as const;

const emptyForm: GlossaryTermCreateRequest = {
  term: '',
  definition: '',
  difficulty_hint: undefined,
  related_terms: [],
  see_also_video_id: null,
  is_published: false,
  slug: '',
};

export default function AdminGlossary() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<GlossaryTermCreateRequest>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'glossary'],
    queryFn: () => glossaryService.adminList(),
  });

  const { data: videos = [] } = useQuery({
    queryKey: ['knowledge', 'videos'],
    queryFn: () => glossaryService.listVideos(),
  });

  const terms = data?.items ?? [];

  const createMutation = useMutation({
    mutationFn: (d: GlossaryTermCreateRequest) => glossaryService.adminCreate(d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'glossary'] });
      setForm(emptyForm);
      setShowForm(false);
      setFormError(null);
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: GlossaryTermCreateRequest }) =>
      glossaryService.adminUpdate(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'glossary'] });
      setEditingId(null);
      setForm(emptyForm);
      setFormError(null);
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => glossaryService.adminDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'glossary'] }),
  });

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setShowForm(true);
  }

  function openEdit(t: GlossaryTermSummary) {
    setEditingId(t.id);
    setForm({
      term: t.term,
      definition: t.definition,
      difficulty_hint: t.difficulty_hint ?? undefined,
      related_terms: t.related_terms ?? [],
      see_also_video_id: t.see_also_video_id ?? null,
      is_published: t.is_published,
      slug: t.slug,
    });
    setFormError(null);
    setShowForm(true);
  }

  function handleSubmit() {
    const payload = {
      ...form,
      slug: form.slug?.trim() || undefined,
    };
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const isBusy = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white">Financial Glossary</h1>
          <p className="text-slate-400 text-sm mt-0.5">{data?.total ?? 0} terms</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={14} />
          Add Term
        </button>
      </div>

      {/* Inline form */}
      {showForm && (
        <div className="mb-6 rounded-xl bg-slate-800/70 border border-slate-700/50 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-white text-sm">
              {editingId ? 'Edit Term' : 'New Term'}
            </h2>
            <button onClick={() => setShowForm(false)}>
              <X size={14} className="text-slate-400 hover:text-white" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Term *</label>
              <input
                value={form.term}
                onChange={e => setForm(p => ({ ...p, term: e.target.value }))}
                placeholder="e.g. Price-to-Earnings Ratio"
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Slug (auto-generated if blank)</label>
              <input
                value={form.slug ?? ''}
                onChange={e => setForm(p => ({ ...p, slug: e.target.value }))}
                placeholder="e.g. price-to-earnings-ratio"
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-slate-400 mb-1 block">Definition *</label>
              <textarea
                value={form.definition}
                onChange={e => setForm(p => ({ ...p, definition: e.target.value }))}
                rows={4}
                placeholder="Plain-language explanation…"
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 resize-none"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Difficulty</label>
              <select
                value={form.difficulty_hint ?? ''}
                onChange={e => setForm(p => ({ ...p, difficulty_hint: e.target.value || undefined }))}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:border-teal-500"
              >
                <option value="">— none —</option>
                {DIFFICULTIES.map(d => (
                  <option key={d} value={d} className="capitalize">{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">See Also Video</label>
              <select
                value={form.see_also_video_id ?? ''}
                onChange={e => setForm(p => ({ ...p, see_also_video_id: e.target.value || null }))}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:border-teal-500"
              >
                <option value="">— none —</option>
                {videos.map(v => (
                  <option key={v.id} value={v.id}>{v.title}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3 pt-5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_published}
                  onChange={e => setForm(p => ({ ...p, is_published: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-teal-500 focus:ring-teal-500"
                />
                <span className="text-sm text-slate-300">Published</span>
              </label>
            </div>
          </div>

          {formError && (
            <p className="text-red-400 text-xs">{formError}</p>
          )}

          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isBusy || !form.term.trim() || !form.definition.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Check size={12} />
              {isBusy ? 'Saving…' : editingId ? 'Save Changes' : 'Create Term'}
            </button>
          </div>
        </div>
      )}

      {/* Terms table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 bg-slate-800/70 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-slate-700/50 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/70">
              <tr>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Term</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium hidden md:table-cell">Slug</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium hidden sm:table-cell">Difficulty</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Published</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/30">
              {terms.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    No glossary terms yet. Add the first one above.
                  </td>
                </tr>
              )}
              {terms.map(t => (
                <tr key={t.id} className="hover:bg-slate-800/30 transition-colors">
                  <td className="px-4 py-3 text-white font-medium">{t.term}</td>
                  <td className="px-4 py-3 text-slate-400 font-mono text-xs hidden md:table-cell">{t.slug}</td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {t.difficulty_hint ? (
                      <span className="capitalize text-xs text-slate-300">{t.difficulty_hint}</span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {t.is_published ? (
                      <span className="text-xs text-teal-400">Yes</span>
                    ) : (
                      <span className="text-xs text-slate-500">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => openEdit(t)}
                        className="p-1.5 text-slate-400 hover:text-white rounded transition-colors"
                        title="Edit"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete "${t.term}"?`)) deleteMutation.mutate(t.id);
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-400 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
